import { FC, useMemo, useState } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { focusMembership } from '../../graphQLOperations/Focus';
import { FocusMembershipQuery } from '../../graphQLOperations/__generated__/FocusMembershipQuery.graphql';
import { usePersistedActiveFocus } from '../../custom-hooks/usePersistedActiveFocus';
import { bucketStudentsByRoadmap, resolveDefaultRoadmapId, resolveDefaultUnitId } from './unitFocusBuckets';
import { UnitFocusRoadmapSegment } from './UnitFocusRoadmapSegment';
import { UnitFocusWidget } from './UnitFocusWidget';
import { BarChart3 } from 'lucide-react';

interface UnitClass {
  id: string;
  name: string;
}

interface Props {
  unitClass: UnitClass;
}

export const UnitFocus: FC<Props> = ({ unitClass }) => {
  const classData = useLazyLoadQuery<FocusMembershipQuery>(
    focusMembership,
    { classId: unitClass.id, validCurrentRoadmapsOnly: true },
    { fetchPolicy: 'store-or-network' },
  );

  const activeFoci = usePersistedActiveFocus(unitClass.id);

  const buckets = useMemo(() => bucketStudentsByRoadmap(classData.class?.users), [classData]);

  const [pickedRoadmapId, setPickedRoadmapId] = useState<string>();
  const [pickedUnitByRoadmap, setPickedUnitByRoadmap] = useState<Record<string, string>>({});

  const persistedActiveTargetIds = useMemo(() => activeFoci.map(f => f.targetId), [activeFoci]);

  if (!buckets.length) {
    return (
      <div className="tw-p-8 tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-center">
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-6">
          <span className="tw-flex tw-p-4 tw-bg-stone-50 tw-rounded-full">
            <BarChart3 size={30} className="tw-text-stone-500" />
          </span>
          <p className="tw-text-gray-600 tw-text-2xl tw-font-atkinson tw-font-bold tw-mb-2">No Unit information yet</p>
          <p className="tw-text-gray-500 tw-text-sm tw-font-atkinson">Data will appear as students get started</p>
        </div>
      </div>
    );
  }

  const selectedRoadmapId = pickedRoadmapId ?? resolveDefaultRoadmapId(activeFoci, buckets);
  const selectedBucket = buckets.find(b => b.id === selectedRoadmapId);
  const selectedUnitId = pickedUnitByRoadmap[selectedRoadmapId] ?? resolveDefaultUnitId(activeFoci, selectedRoadmapId);

  return (
    <>
      <UnitFocusRoadmapSegment buckets={buckets} selectedRoadmapId={selectedRoadmapId} onSelect={setPickedRoadmapId} />
      {selectedRoadmapId && (
        <UnitFocusWidget
          selectedRoadmapId={selectedRoadmapId}
          roadmapName={selectedBucket?.name ?? unitClass.name}
          persistedActiveTargetIds={persistedActiveTargetIds}
          selectedUnitId={selectedUnitId}
          onSelectUnit={unitId => setPickedUnitByRoadmap(prev => ({ ...prev, [selectedRoadmapId]: unitId }))}
        />
      )}
    </>
  );
};
